import castArray from 'lodash/castArray';
import mime from 'mime';
import { v4 as uuidv4 } from 'uuid';
import { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Button, Icon, Spinner, Tooltip } from '@kolking/react-ui';

import { type ImageData } from '../../models';
import styles from './styles.module.scss';

const MAX_FILES = 5;

interface ThumbnailProps {
  data: ImageData;
  onDelete: (data: ImageData) => void;
}

const Thumbnail = ({ data, onDelete }: ThumbnailProps) => {
  const [src, setSrc] = useState<string>();
  const [error, setError] = useState<string>();

  const { file } = data;

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    (async () => {
      let blob: Blob;

      const resolvedType = file.type || mime.getType(file.name) || '';
      const isHeif = resolvedType === 'image/heic' || resolvedType === 'image/heif';

      if (isHeif) {
        const header = new Uint8Array(await file.slice(0, 3).arrayBuffer());

        // iOS sometimes sends .jpeg file with .heic extension, make sure the file is actually .heif
        const isJpeg = header[0] === 0xff && header[1] === 0xd8 && header[2] === 0xff;

        if (isJpeg) {
          blob = file;
        } else {
          const { default: heic2any } = await import('heic2any');
          const blobs = await heic2any({
            blob: file,
            toType: 'image/jpeg',
            quality: 0.5,
          });

          const previewBlobs = castArray(blobs);

          if (previewBlobs.length > 0) {
            blob = previewBlobs[0];
          } else {
            throw new Error();
          }
        }
      } else {
        blob = file;
      }

      if (!signal.aborted) {
        const objectUrl = URL.createObjectURL(blob);

        signal.addEventListener('abort', () => {
          URL.revokeObjectURL(objectUrl);
        });

        setSrc(objectUrl);
      }
    })().catch(() => {
      setError('Thumbnail generation error');
    });

    return () => {
      controller.abort();
    };
  }, [file]);

  const handleDelete = useCallback(() => onDelete(data), [data, onDelete]);

  return (
    <figure className={styles.thumbnail}>
      {src ? (
        <img src={src} alt="" />
      ) : error ? (
        <Tooltip content={error}>
          <Icon size="35%" name="denied" />
        </Tooltip>
      ) : (
        <Spinner size={24} />
      )}
      <Button
        type="button"
        scheme="negative"
        className={styles.delete}
        icon={<Icon name="cross" />}
        onClick={handleDelete}
      />
    </figure>
  );
};

export interface ImageUploadProps {
  files?: ImageData[];
  onChange: (files: ImageData[]) => void;
}

export const ImageUpload = ({ files = [], onChange }: ImageUploadProps) => {
  const { getRootProps, getInputProps, isDragAccept, isDragReject } = useDropzone({
    maxFiles: MAX_FILES,
    accept: {
      'image/*': ['.gif', '.jpg', '.jpeg', '.png', '.svg', '.heic', '.heif'],
    },
    onDrop: (acceptedFiles) => {
      const images: ImageData[] = [...files];

      for (const file of acceptedFiles) {
        const extension = mime.getExtension(file.type);
        const fileName = extension ? `${uuidv4()}.${extension}` : uuidv4();

        images.push({
          file,
          url: `/uploads/token/${fileName}`,
        });
      }

      onChange(images);
    },
  });

  const handleDelete = useCallback(
    (file: ImageData) => {
      onChange(files.filter((v) => v !== file));
    },
    [files, onChange],
  );

  if (files.length === 0) {
    return (
      <div
        {...getRootProps({ className: styles.dropzone })}
        data-accept={isDragAccept || undefined}
        data-reject={isDragReject || undefined}
      >
        <input {...getInputProps()} />
        <small>
          {isDragAccept
            ? 'Drop the file here'
            : isDragReject
              ? 'This file is not allowed'
              : 'Drag images here, or click to select files'}
        </small>
      </div>
    );
  }

  return (
    <div className={styles.photos}>
      {files.map((data, index) => (
        <Thumbnail key={index} data={data} onDelete={handleDelete} />
      ))}
      {files.length < MAX_FILES && (
        <div
          {...getRootProps({ className: styles.addnew })}
          data-accept={isDragAccept || undefined}
          data-reject={isDragReject || undefined}
        >
          <input {...getInputProps()} />
          <Icon size="35%" name="camera" />
        </div>
      )}
    </div>
  );
};
